import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertTriangle, Package, Warehouse as WarehouseIcon, Search, RefreshCw } from "lucide-react";
import LotesProductoDialog from "@/components/LotesProductoDialog";

const PAGE_SIZE = 20;

export default function Warehouse() {
  const [stock, setStock] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [resumen, setResumen] = useState({});
  const [verTodas, setVerTodas] = useState(false);
  const [search, setSearch] = useState("");
  const [lotesView, setLotesView] = useState(null); // { id, nombre }
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const primeraCarga = useRef(true);

  // ITER210: los datos de stock se refrescan solos (al buscar, al volver a la
  // pestaña, cada 60 s y al cerrar el detalle de lotes) para no depender de F5.
  const load = useCallback(async (silencioso = false) => {
    if (!silencioso) setRefreshing(true);
    try {
      const [s, a, r] = await Promise.all([
        api.get("/warehouse/stock"),
        api.get("/warehouse/alertas-vencimiento?dias=180"),
        api.get("/warehouse/resumen"),
      ]);
      setStock(s.data); setAlertas(a.data); setResumen(r.data);
      setUpdatedAt(new Date());
    } catch (e) {
      if (!silencioso) toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refrescar al escribir en el buscador (debounce) para ver valores al día
  useEffect(() => {
    if (primeraCarga.current) { primeraCarga.current = false; return; }
    const t = setTimeout(() => load(true), 450);
    return () => clearTimeout(t);
  }, [search, load]);

  // Refrescar al volver a la pestaña y cada 60 s mientras esté visible
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const id = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(id);
    };
  }, [load]);

  const alertasVisibles = verTodas ? alertas : alertas.slice(0, PAGE_SIZE);
  const q = search.trim().toLowerCase();
  const stockFiltrado = q
    ? stock.filter(s => [s.codigo_interno, s.nombre_comercial].filter(Boolean).join(" ").toLowerCase().includes(q))
    : stock;
  const alertasFiltradas = q
    ? alertasVisibles.filter(a => [a.producto_nombre, a.numero_lote].filter(Boolean).join(" ").toLowerCase().includes(q))
    : alertasVisibles;

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Bodega</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Stock y lotes</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Vista consolidada de inventario, con regla FEFO y alertas por vencimiento.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#5c7368] font-semibold mb-1">Productos</div>
          <div className="text-2xl font-bold">{resumen.total_productos ?? 0}</div>
        </div>
        <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#5c7368] font-semibold mb-1">Lotes activos</div>
          <div className="text-2xl font-bold">{resumen.total_lotes_activos ?? 0}</div>
        </div>
        <div className="rounded-md border border-[#d99584] bg-[#fdecea] p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#7a2514] font-semibold mb-1">Vencidos</div>
          <div className="text-2xl font-bold text-[#7a2514]">{resumen.vencidos ?? 0}</div>
        </div>
        <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#8a5500] font-semibold mb-1">Críticos (≤30d)</div>
          <div className="text-2xl font-bold text-[#8a5500]">{resumen.criticos_30d ?? 0}</div>
        </div>
        <div className="rounded-md border border-[#d1dbd6] bg-white p-4" data-testid="stat-proximos-180d">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#5c7368] font-semibold mb-1">Próximos a caducar (≤180d)</div>
          <div className="text-2xl font-bold">{resumen.proximos_180d ?? 0}</div>
          <div className="text-[10px] text-[#8ba397] mt-1">de los cuales {resumen.proximos_90d ?? 0} en 90d</div>
        </div>
      </div>

      <Tabs defaultValue="stock">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="stock"><Package size={14} className="mr-2" />Stock por producto</TabsTrigger>
          <TabsTrigger value="alertas"><AlertTriangle size={14} className="mr-2" />Alertas de vencimiento</TabsTrigger>
        </TabsList>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8ba397]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar producto o lote…"
              className="pl-8"
              data-testid="warehouse-search-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={refreshing} data-testid="warehouse-refresh-btn">
            <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Actualizando…" : "Actualizar"}
          </Button>
          {updatedAt && (
            <span className="text-[11px] text-[#5c7368]" data-testid="warehouse-updated-at">
              Datos al {updatedAt.toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>

        <TabsContent value="stock" className="mt-4">
          <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
            <table className="erp-table">
              <thead><tr><th>Código</th><th>Producto</th><th className="text-right">Stock</th><th className="text-right">Cajas / Sueltas</th><th className="text-right"># Lotes</th><th>Próx. vencimiento</th><th className="w-10"></th></tr></thead>
              <tbody>
                {stockFiltrado.map((s, i) => {
                  const upc = s.unidades_por_presentacion || 1;
                  const total = parseInt(s.stock_total) || 0;
                  const cajas = upc > 1 ? Math.floor(total / upc) : 0;
                  const sueltas = upc > 1 ? total - cajas * upc : 0;
                  return (
                    <tr
                      key={i}
                      onClick={() => setLotesView({ id: s.producto_comercial_id, nombre: s.nombre_comercial })}
                      className="cursor-pointer hover:bg-[#f0f4f2]"
                      data-testid={`stock-row-${i}`}
                    >
                      <td className="font-mono text-xs">{s.codigo_interno}</td>
                      <td className="font-medium">{s.nombre_comercial}</td>
                      <td className="text-right tabular-nums font-semibold">
                        {total}<span className="text-[10px] font-normal text-[#5c7368]"> u.</span>
                      </td>
                      <td className="text-right tabular-nums">
                        {upc > 1 ? (
                          <span className="text-[#205c45] text-xs">
                            {cajas > 0 && <span><strong>{cajas}</strong> caja{cajas !== 1 ? "s" : ""}</span>}
                            {cajas > 0 && sueltas > 0 && <span className="text-[#5c7368]"> + </span>}
                            {sueltas > 0 && <span><strong>{sueltas}</strong> suelta{sueltas !== 1 ? "s" : ""}</span>}
                            {cajas === 0 && sueltas === 0 && <span className="text-[#5c7368]">·</span>}
                          </span>
                        ) : <span className="text-[#8ba397] text-[10px]">N/A</span>}
                      </td>
                      <td className="text-right tabular-nums">{s.lotes_count}</td>
                      <td>{s.prox_vencimiento}</td>
                      <td className="text-[10px] text-[#205c45] font-semibold">Ver lotes →</td>
                    </tr>
                  );
                })}
                {stockFiltrado.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]"><WarehouseIcon className="inline mb-2" size={28} /><br />{q ? `Sin resultados para "${search}"` : "Sin inventario aún"}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="alertas" className="mt-4">
          <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
            <table className="erp-table">
              <thead><tr><th>Producto</th><th>Lote</th><th>Vencimiento</th><th className="text-right">Disponible</th><th>Alerta</th></tr></thead>
              <tbody>
                {alertasFiltradas.map((a, i) => {
                  const cls = a.alerta === "vencido" ? "fefo-vencido" : a.alerta === "critico" ? "fefo-critico" : "fefo-safe";
                  const upc = a.unidades_por_presentacion || 1;
                  const total = parseInt(a.cantidad_disponible) || 0;
                  const cajas = upc > 1 ? Math.floor(total / upc) : 0;
                  const sueltas = upc > 1 ? total - cajas * upc : 0;
                  return (
                    <tr key={i} data-testid={`alert-row-${i}`}>
                      <td className="font-medium">{a.producto_nombre}</td>
                      <td className="font-mono text-xs">{a.numero_lote}</td>
                      <td>{a.fecha_vencimiento}</td>
                      <td className="text-right tabular-nums whitespace-nowrap">
                        <div className="font-semibold">{total}<span className="text-[10px] font-normal text-[#5c7368]"> u.</span></div>
                        {upc > 1 && (cajas > 0 || sueltas > 0) && (
                          <div className="text-[10px] text-[#205c45]">
                            {cajas > 0 && <span><strong>{cajas}</strong>c</span>}
                            {cajas > 0 && sueltas > 0 && <span> + </span>}
                            {sueltas > 0 && <span><strong>{sueltas}</strong>s</span>}
                          </div>
                        )}
                      </td>
                      <td><Badge className={`${cls} border capitalize`}>{a.alerta}</Badge></td>
                    </tr>
                  );
                })}
                {alertasFiltradas.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8ba397]">{q ? `Sin resultados para "${search}"` : "Sin alertas. Todos los lotes están en buen estado."}</td></tr>}
              </tbody>
            </table>
            {alertas.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#d1dbd6] bg-[#f7f9f8]">
                <span className="text-xs text-[#5c7368]">
                  Mostrando <strong>{alertasFiltradas.length}</strong> de <strong>{alertas.length}</strong> alertas
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVerTodas(v => !v)}
                  data-testid="ver-todas-alertas-btn"
                >
                  {verTodas ? "Mostrar solo 20" : `Ver todas (${alertas.length})`}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {lotesView && (
        <LotesProductoDialog
          open={!!lotesView}
          onOpenChange={(v) => { if (!v) { setLotesView(null); load(true); } }}
          productoId={lotesView.id}
          productoNombre={lotesView.nombre}
        />
      )}
    </div>
  );
}
